import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core"
import { ServiceInstance } from "app/models/service-instance";
import { Observable } from "rxjs";
import { AuthenticationService } from "./authentication.service";
import { ServiceService } from "./service.service";
import { MmcPrintDispatch } from "app/models/mmc/mmc-print-dispatch";
import { Url_Base } from "app/rest-constants";

/** Kiss API Path for obtaining the Material Info for a given Material Number */
const KissApiPath_GetMaterialInfo =  "https://apidev.kissusa.com/rfid/material_num";

/** UUID of the Kiss Service Instance created in Edgefinity.  Holds configuration / setting values. */
const EfKissServiceUuid = "4bd27eac-bea7-44f1-ac91-e3b244f93b78";

/** Service Property Keys of various Properties for the Kiss Service */
const ServicePropertyKey_KissApiAuthToken = "kissapi_authtoken";
const ServicePropertyKey_KissApiUserId = "kissapi_userid";
const ServicePropertyKey_UiCallKissDirectly = "ui_call_kiss";
const ServicePropertyKey_UseMmc = "use_mmc";


/**
 * Service for interacting with all the custom KISS methods / functionality
 * provided by Edgefinity.
 * 
 * This stores a cached reference to the "Kiss Service" Instance in Edgefinity which can be
 * refreshed by calling the "refreshKissServiceReference()" method.  (This is currently done
 * when the User navigates to the "Kiss Print Page")
 */
@Injectable()
export class KissService {

    /** Cached instance of the KISS Service */
    private kissService: ServiceInstance = null;

    /** Constructor */
    constructor(
        private authenticationService: AuthenticationService,
        private httpClient: HttpClient,
        private serviceService: ServiceService
    ) { }

    /**
     * Refreshes the cached reference to the "Kiss Service" Instance
     */
    public async refreshKissServiceReference(): Promise<void> {
        const kissService = await this.serviceService.getServiceInstanceByUuid(EfKissServiceUuid);
        if (kissService == null || kissService == undefined) {
            throw "Could not find EF Kiss Service!";
        } else {
            this.kissService = kissService;
        }
    }

    /**
     * Refreshes the cached reference to the "Kiss Service" Instance
     * if that cached reference hasn't been obtained yet.
     */
    private async refreshKissServiceReferenceIfNeeded(): Promise<void> {
        if (this.kissService == null || this.kissService == undefined) {
            await this.refreshKissServiceReference();
        }
    }

    /**
     * Returns a boolean indicating if the 
     */
    public async isMmcBeingUsed(): Promise<boolean> {
        // Refresh the reference to the KISS Service (if needed)
        await this.refreshKissServiceReferenceIfNeeded();

        // Return the value of the "Use MMC" Service Property
        return this.getServiceProperty_UseMmc();
    }

    /**
     * Obtains the Material Info for the given Material Number.
     * 
     * This takes into account the "Kiss Service" Instance's "UI call KISS Directly" Property / Setting.
     * Depending on that value, this either calls the KISS API directly to obtain the information or calls
     * the proxy API running in Edgefinity.  
     * (We were having CORS issues when having the UI call it directly so made this a setting)
     * 
     * @param materialNumber 
     * @returns 
     */
    public async getMaterialInfo(materialNumber: string): Promise<GetMaterialInfoResponseDto> {
        // Refresh the reference to the KISS Service (if needed)
        await this.refreshKissServiceReferenceIfNeeded();

        // Validate that a Material Number was passed
        if (materialNumber == null || materialNumber.trim().length == 0) {
            throw { error: "Material Number required." };
        }

        // Depending on the "Call Kiss UI Directly" Property setting, either call KISS directly or call the EF API
        const callKissDirectly = this.getServiceProperty_UiCallKissDirectly();
        if (callKissDirectly) {
            return this.getMaterialInfoFromKissDirectly(materialNumber);
        } else {
            return this.getMaterialInfoFromEf(materialNumber);
        }
    }

    /**
     * Calls the KISS API directly to obtain the Material Info for the given Material Number.
     */
    private async getMaterialInfoFromKissDirectly(materialNumber: string): Promise<GetMaterialInfoResponseDto> {
        // Constants used to make the API call against Kiss directly
        const callUrl = `${KissApiPath_GetMaterialInfo}?materialNum=${materialNumber}`;
        const httpHeaders = new HttpHeaders({
            'Accept': 'application/json',
            'Authorization': this.getServiceProperty_AuthorizationToken(),
            'Userid': this.getServiceProperty_UserId()
        });

        // Call the KISS API directly...
        let kissResponse: KissMaterialInfoResponse;
        try {
            kissResponse  = (await this.httpClient
                .get(callUrl, { headers: httpHeaders })
                .toPromise()) as KissMaterialInfoResponse;
        } catch (error) {
            // Special Error handling for the CORS issue to make it more obvious to Users
            if (error?.status == 0) {
                throw { error: 'Problem obtaining Material Info from KISS directly.  Most likely a CORS violation.' };
            } else {
                throw error;
            }
        }

        // Validate the response from KISS
        if (kissResponse == null || kissResponse == undefined) {
            throw "No response from KISS.";
        } else if (kissResponse.result == null || kissResponse.result == false) {
            throw "Failed to obtain info from KISS.  Error: " + kissResponse.error_code;
        }

        // Parse the response from KISS into the GetMaterialInfoResponseDto and return it
        const rv = {} as GetMaterialInfoResponseDto;
        rv.material_number = kissResponse.data[0].T_UOM[0].MATNR;
        rv.material_description = kissResponse.data[0].T_UOM[0].MAKTX;
        rv.uoms = [];
        for (const uom of kissResponse.data[0].T_UOM) {
            const dto = {} as UomDto;
            dto.unit_code = uom.MEINH;
            dto.ean = uom.EAN11;
            dto.pack_quantity = uom.UMREZ;
            rv.uoms.push(dto);
        }
        return rv;
    }

    /**
     * Calls the Edgefinity proxy API which then calls the KISS API to obtain the 
     * Material Info for the given Material Number.
     */
    private getMaterialInfoFromEf(materialNumber: string): Promise<GetMaterialInfoResponseDto> {
        const callUrl = `${Url_Base}/kiss/get-material-info`;
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': this.authenticationService.getToken()
          });
        const body = { material_number: materialNumber } as GetMaterialInfoRequestDto;
        return this.httpClient.post<GetMaterialInfoResponseDto>(callUrl, body, { headers: headers }).toPromise();
    }

    /**
     * Calls the Edgefinity API to obtain the possible values for the "Printer" in KISS's "Print" page.
     */
    public async getAvailablePrinters(): Promise<string[]> {
        // Refresh the reference to the KISS Service (if needed)
        await this.refreshKissServiceReferenceIfNeeded();

        // Get the "Use MMC" Property Value to determine which set of Printers to return
        const useMmc = this.getServiceProperty_UseMmc();
        if (useMmc) {
            return this.getAvailablePrintersForMmc().toPromise();
        } else {
            return this.getAvailablePrintersForMmpi().toPromise();
        }
    }

    private getAvailablePrintersForMmc(): Observable<string[]> {
        const requestUrl = `${Url_Base}/kiss/print/mmc/available-printers`;
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': this.authenticationService.getToken()
          });
        return this.httpClient.get<string[]>(requestUrl, { headers: headers });
    }

    private getAvailablePrintersForMmpi(): Observable<string[]> {
        const requestUrl = `${Url_Base}/kiss/print/mmpi/available-printers`;
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': this.authenticationService.getToken()
          });
        return this.httpClient.get<string[]>(requestUrl, { headers: headers });
    }

    /**
     * Calls the Edgefinity API to print the labels with the given Print information using MMC.
     */
    public printUsingMmc(printRequest: PrintMaterialsRequestDto): Observable<MmcPrintDispatch> {
        // Call the MMC Print API 
        const requestUrl = `${Url_Base}/kiss/print/mmc/print-material`;
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': this.authenticationService.getToken()
          });
        return this.httpClient.post<MmcPrintDispatch>(requestUrl, printRequest, {headers: headers});
    }

    /**
     * Calls the Edgefinity API to print the labels with the given Print information.
     */
    public printUsingMmpi(printRequest: PrintMaterialsRequestDto): Observable<PrintMaterialsResponseDto> {
        // Call the MMC Print API 
        const requestUrl = `${Url_Base}/kiss/print/mmpi/print-material`;
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': this.authenticationService.getToken()
          });
        return this.httpClient.post<PrintMaterialsResponseDto>(requestUrl, printRequest, {headers: headers});
    }

    /**
     * Calls the Edgefinity API to get the order information for the Portal Validation process.
     */
    public getOrderInfo(request: GetOrderInformationRequestDto): Observable<any> {
        const requestUrl = `${Url_Base}/kiss/get-order-info`;
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': this.authenticationService.getToken()
          });
        return this.httpClient.post<any>(requestUrl, request, {headers: headers});
    }

    public getValidationInfo(portalId: number | string): Observable<ValidationInfo> {
        const requestUrl = `${Url_Base}/kiss/portal/${portalId}/get-info`;
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': this.authenticationService.getToken()
          });
        return this.httpClient.post<any>(requestUrl, null, {headers: headers});
    }

    public enterOrderInfo(portalId: number, request: GetOrderInformationRequestDto): Observable<ValidationInfo> {
        const requestUrl = `${Url_Base}/kiss/portal/${portalId}/enter-order-info`;
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': this.authenticationService.getToken()
          });
        return this.httpClient.post<any>(requestUrl, request, {headers: headers});
    }

    public clearOrderInfo(portalId: number | string): Observable<ValidationInfo> {
        const requestUrl = `${Url_Base}/kiss/portal/${portalId}/clear-order`;
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': this.authenticationService.getToken()
          });
        return this.httpClient.post<any>(requestUrl, null, {headers: headers});
    }

    public getReaderState(portalId: number | string): Observable<KissReaderStateDto> {
        const requestUrl = `${Url_Base}/kiss/portal/${portalId}/get-reader-state`;
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': this.authenticationService.getToken()
          });
        return this.httpClient.get<KissReaderStateDto>(requestUrl, {headers: headers});
    }

    public startReader(portalId: number | string): Observable<void> {
        const requestUrl = `${Url_Base}/kiss/portal/${portalId}/start-reader`;
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': this.authenticationService.getToken()
          });
        return this.httpClient.post<void>(requestUrl, null, {headers: headers});
    }

    public stopReader(portalId: number | string): Observable<void> {
        const requestUrl = `${Url_Base}/kiss/portal/${portalId}/stop-reader`;
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': this.authenticationService.getToken()
          });
        return this.httpClient.post<void>(requestUrl, null, {headers: headers});
    }

    public processValidation(portalId: number | string, lineItems: KissPortalValidationProcessLineItem[]): Observable<void> {
        const requestUrl = `${Url_Base}/kiss/portal/${portalId}/process`;
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': this.authenticationService.getToken()
          });
        return this.httpClient.post<void>(requestUrl, lineItems, {headers: headers});
    }



    public getOutboundState(portalId: number | string): Observable<KissOutboundStateInfo> {
      const requestUrl = `${Url_Base}/kiss/outbound/${portalId}/get-state`;
      const headers = new HttpHeaders({
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': this.authenticationService.getToken()
        });
        return this.httpClient.post<KissOutboundStateInfo>(requestUrl, null, { headers: headers});
    }

    public enterOutboundInfo(portalId: number, request: KissOutboundGetContainerDetailsDto): Observable<KissOutboundStateInfo> {
        const requestUrl = `${Url_Base}/kiss/outbound/${portalId}/enter-info`;
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': this.authenticationService.getToken()
          });
        return this.httpClient.post<KissOutboundStateInfo>(requestUrl, request, {headers: headers});
    }

    public startOutboundReader(portalId: number | string): Observable<void> {
        const requestUrl = `${Url_Base}/kiss/outbound/${portalId}/start-reader`;
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': this.authenticationService.getToken()
          });
        return this.httpClient.post<void>(requestUrl, null, {headers: headers});
    }

    public stopOutboundReader(portalId: number | string): Observable<void> {
        const requestUrl = `${Url_Base}/kiss/outbound/${portalId}/stop-reader`;
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': this.authenticationService.getToken()
          });
        return this.httpClient.post<void>(requestUrl, null, {headers: headers});
    }

    public clearOutboundReads(portalId: number): Observable<KissOutboundStateInfo> {
        const requestUrl = `${Url_Base}/kiss/outbound/${portalId}/clear-reads`;
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': this.authenticationService.getToken()
          });
        return this.httpClient.post<KissOutboundStateInfo>(requestUrl, null, {headers: headers});
    }

    public clearOutboundAttempt(portalId: number): Observable<KissOutboundStateInfo> {
        const requestUrl = `${Url_Base}/kiss/outbound/${portalId}/clear-attempt`;
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': this.authenticationService.getToken()
          });
        return this.httpClient.post<KissOutboundStateInfo>(requestUrl, null, {headers: headers});
    }

    public processOutboundAttempt(portalId: number): Observable<KissOutboundStateInfo> {
        const requestUrl = `${Url_Base}/kiss/outbound/${portalId}/process-attempt`;
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': this.authenticationService.getToken()
          });
        return this.httpClient.post<KissOutboundStateInfo>(requestUrl, null, {headers: headers});
    }



    /**
     * Returns the value of the "Authorization Token" Property for cached Kiss Service Instance
     */
    private getServiceProperty_AuthorizationToken(): string {
        for (const datum of this.kissService?.data) {
            if (datum.property.property == ServicePropertyKey_KissApiAuthToken) {
                return datum.value;
            }
        }
        return null;
    }

    /**
     * Returns the value of the "User ID" Property for cached Kiss Service Instance
     */
    private getServiceProperty_UserId(): string {
        for (const datum of this.kissService?.data) {
            if (datum.property.property == ServicePropertyKey_KissApiUserId) {
                return datum.value;
            }
        }
        return null;
    }

    /**
     * Returns the value of the "UI Call Kiss Directly" Property for cached Kiss Service Instance
     */
    private getServiceProperty_UiCallKissDirectly(): boolean {
        for (const datum of this.kissService?.data) {
            if (datum.property.property == ServicePropertyKey_UiCallKissDirectly) {
                return datum.value == 'true';
            }
        }
        return false;
    }

    /**
     * Returns the value of the "UI Call Kiss Directly" Property for cached Kiss Service Instance
     */
    private getServiceProperty_UseMmc(): boolean {
        for (const datum of this.kissService?.data) {
            if (datum.property.property == ServicePropertyKey_UseMmc) {
                return datum.value == 'true';
            }
        }
        return false;
    }

}

/** UUID of the 'Kiss Portal Validation' Service Type */
export const KissPortalServiceTypeUuid: string = '4e687a90-5dfa-4979-9bb1-03fd61479ca7';

/** UUID of the 'Kiss Outbound Validation' Service Type */
export const KissOutboundServiceTypeUuid: string = '019bc789-cc9b-73e7-ad1c-d45b94961dae';

/** Interface representing the request when calling Edgefinity during a "GetMaterialInfo" Request */
export interface GetMaterialInfoRequestDto {
    material_number: string;
}

/** Interface representing the response when calling Edgefinity during a "GetMaterialInfo" Request */
export interface GetMaterialInfoResponseDto {
    material_number: string;
    material_description: string;
    uoms: UomDto[];
}

/** Interface representing a single UOM returned by a "GetMaterialInfo" Request */
export interface UomDto {
    unit_code: string;
    pack_quantity: number;
    ean: string;
}

/** Interface representing the request when calling Edgefinity during a "PrintMaterials" Request */
export interface PrintMaterialsRequestDto {
    material_number: string;
    material_description: string;
    batch_number: string;
    lot_number: string;
    pao: string;
    validation_date: string;
    misc01: string;
    misc02: string;
    misc03: string;
    misc04: string;
    misc05: string;
    uom: UomDto;
    printer: string;
    quantity: number;
}

/** Interface representing the response when calling Edgefinity during a "PrintMaterials" Request */
export interface PrintMaterialsResponseDto {
    result: boolean;
    message: string;
}

/** Interface representing the response from KISS used when calling KISS directly during a "GetMaterialInfo" Request. */
export interface KissMaterialInfoResponse {
    result: boolean;
    error_code: string;
    data: Kiss_MaterialInfoResponse_Data[];
}

/** Interface representing a "data" object returned in the KissMaterialInfoResponse */
export interface Kiss_MaterialInfoResponse_Data {
    E_IFMSG: string;
    T_UOM: Kiss_T_Uom[];
}

/** Interface representing a "T_UOM" object returned in the Kiss_MaterialInfoResponse_Data */
export interface Kiss_T_Uom {
    MATNR: string;
    MAKTX: string;
    MEINH: string;
    UMREZ: number;
    UMREN: number;
    EAN11: string;
    LAENG: number;
    BREIT: number;
    HOEHE: number;
    BRGEW: number;
    GEWEI: string;
    HERKL: string;
}

/**
 * Used by Kiss's Portal Validation process. 
 */
export interface GetOrderInformationRequestDto {
    order_number: string;
    transport_id: string;
    vendor_invoice: string;
    bill_of_lading: string;
    inbound_delivery: string;
}

export enum ValidationState {
    EnterOrderInfo = 'EnterOrderInfo',
    RetrievingOrderInfo = 'RetrievingOrderInfo',
    OrderBeingDisplayed = 'OrderBeingDisplayed',
    ValidationBeingProcessed = 'ValidationBeingProcessed',
    DisplayResults = 'DisplayResults'
}

export enum ValidationOrderType {
    OrderNumber = 'OrderNumber',
    TransportId = 'TransportId',
    BillOfLading = 'BillOfLading',
    VendorInvoice = 'VendorInvoice',
    InboundDelivery = 'InboundDelivery'
}

export interface ValidationOrderIdentityInfo {
    type: ValidationOrderType;
    value: string;
}

export interface VI_LineItem {
    asn: string;
    po: string;
    asn_line_number: string;
    material_number: string;
    material_description: string;
    required_quantity: number;
    scanned_quantity: number;
    completed_quantity: number;
    balance_quantity: number;
    record_date: string;
    sub_rows: VI_LineItemSubRow[];
    uuid: string;
}

export interface VI_LineItemSubRow {
    company: string;
    batch: string;
    scanned_quantity: number;
}

export interface ValidationInfo {
    state: ValidationState;
    order_identity: ValidationOrderIdentityInfo;
    line_items: VI_LineItem[];
}

export enum KissReaderStateEnum {
    Disconnected = 'Disconnected',
    Stopped = 'Stopped',
    Started = 'Started',
    Reconnecting = 'Reconnecting...',
    Restarting = 'Restarting...'
}

export interface KissReaderStateDto {
    reader_state: KissReaderStateEnum;
}

export interface KissPortalValidationProcessLineItem {
    uuid: string;
    scanned_quantity_override: number | null;
}


/*
 * Used by KISS's Outbound Validation Process
 */
export interface KissOutboundStateInfo {
  state: KissOutboundState;
  reader_state: KissReaderStateEnum;
  current_attempt: KissOutboundAttemptDto;
}

export enum KissOutboundState {
  /** 
   * Waiting on Carton ID to be entered to lookup the 
   * Container information.
   */
  WaitingOnContainerId = 'WaitingOnContainerId',

  /**
   * Container information has been looked up and can have
   * Tag Read(s) associated to it.
   */
  AttemptBeingValidated = 'AttemptBeingValidated'
}

export interface KissOutboundGetContainerDetailsDto {
    container_id: string;
}

export interface KissOutboundAttemptDto {
  container_id: string;
  line_items: KissOutboundAttemptLineItemDto[];
}

export interface KissOutboundAttemptLineItemDto {
  item: string;
  upcs: string[];
  batch: string;
  expected_quantity: number;
  counted_quantity: number;
}
